import Component from '../Component';
import fireDebugWarning from '../helpers/fireDebugWarning';
import classList from '../utils/classList';
import extractText from '../utils/extractText';
import LoadingIndicator from './LoadingIndicator';
import Icon from './Icon';
/**
 * The `Button` component defines an element which, when clicked, performs an
 * action.
 *
 * Other attrs will be assigned as attributes on the `<button>` element.
 *
 * Note that a Button has no default class names. This is because a Button can
 * be used to represent any generic clickable control, like a menu item. Common
 * styles can be applied by providing `className="Button"` to the Button component.
 */
export default class Button extends Component {
  view(vnode) {
    let {
      type,
      'aria-label': ariaLabel,
      icon: iconName,
      disabled,
      loading,
      className,
      class: _class,
      ...attrs
    } = this.attrs;

    // If no `type` attr provided, set to "button"
    type ||= 'button';

    // If given a translation object, extract the text.
    if (typeof ariaLabel === 'object') {
      ariaLabel = extractText(ariaLabel);
    }
    if (disabled || loading) {
      delete attrs.onclick;
    }
    className = classList(_class, className, {
      hasIcon: iconName,
      disabled: disabled || loading,
      loading: loading
    });
    const buttonAttrs = {
      disabled,
      className,
      type,
      'aria-label': ariaLabel,
      ...attrs
    };
    return m("button", buttonAttrs, this.getButtonContent(vnode.children));
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    const {
      'aria-label': ariaLabel
    } = this.attrs;
    if (this.view === Button.prototype.view && !ariaLabel && !extractText(vnode.children) && !this.element?.getAttribute?.('aria-label')) {
      fireDebugWarning('[Flarum Accessibility Warning] Button has no content and no accessible label. This means that screen-readers will not be able to interpret its meaning and just read "Button". Consider providing accessible text via the `aria-label` attribute. https://web.dev/button-name', this.element);
    }
  }

  /**
   * Get the template for the button's content.
   */
  getButtonContent(children) {
    const iconName = this.attrs.icon;
    return [iconName && m(Icon, {
      name: iconName,
      className: "Button-icon"
    }), children && m("span", {
      className: "Button-label"
    }, children), this.attrs.loading && m(LoadingIndicator, {
      size: "small",
      display: "inline"
    })];
  }
}
flarum.reg.add('core', 'common/components/Button', Button);